import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { ExternalLink, Trash2 } from "lucide-react";

import { Button } from "@monorepo-apps/ui/src/components/button";
import { Checkbox } from "@monorepo-apps/ui/src/components/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@monorepo-apps/ui/src/components/dialog";
import { Input } from "@monorepo-apps/ui/src/components/input";
import { Label } from "@monorepo-apps/ui/src/components/label";
import { Textarea } from "@monorepo-apps/ui/src/components/textarea";

import {
  useDeleteFeature,
  useFeatureDetail,
  useUpdateFeature,
} from "@/application/use-cases/features/use-features.queries";
import { supabase } from "@/infrastructure/supabase/client";
import type { FeatureRow } from "@/infrastructure/supabase/database.types";
import { DomainPill } from "@/presentation/components/roadmaps/domain-pill";
import { getArchitectureNodeById } from "@/presentation/data/architecture-map-data";
import type { Domain } from "@/presentation/data/domains";

interface FeatureInfoDialogProps {
  feature: FeatureRow | null;
  onClose: () => void;
}

interface SourceMeeting {
  id: string;
  title: string;
  slug: string;
}

async function fetchSourceMeeting(featureId: string): Promise<SourceMeeting | null> {
  const { data, error } = await supabase
    .from("meeting_links")
    .select("meetings(id, title, slug)")
    .eq("entity_type", "feature")
    .eq("entity_id", featureId)
    .order("created_at", { ascending: true })
    .limit(1)
    .maybeSingle();
  if (error || !data) return null;
  const m = (data as { meetings: SourceMeeting | SourceMeeting[] | null }).meetings;
  if (!m) return null;
  return Array.isArray(m) ? (m[0] ?? null) : m;
}

export function FeatureInfoDialog({ feature, onClose }: FeatureInfoDialogProps) {
  return (
    <Dialog open={feature !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[80vh] max-w-2xl overflow-y-auto">
        {feature ? (
          <FeatureInfoBody key={feature.id} featureId={feature.id} onDeleted={onClose} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

/**
 * Destructive delete control. Two-step: button → confirm row with cancel +
 * confirm. Kept inline (no AlertDialog) to avoid stacking modal layers.
 */
function FeatureDeleteControl({
  featureId,
  featureTitle,
  onDeleted,
}: {
  featureId: string;
  featureTitle: string;
  onDeleted: () => void;
}) {
  const [armed, setArmed] = useState(false);
  const deleteFeature = useDeleteFeature();

  if (!armed) {
    return (
      <div className="border-t pt-3">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="text-destructive hover:bg-destructive/10 hover:text-destructive gap-1.5"
          onClick={() => setArmed(true)}
        >
          <Trash2 className="size-3.5" />
          Delete feature
        </Button>
      </div>
    );
  }

  return (
    <div className="border-destructive/30 bg-destructive/5 flex flex-wrap items-center justify-between gap-2 rounded-md border px-3 py-2.5">
      <p className="text-foreground/85 text-[12px] leading-snug">
        Permanently delete <strong className="font-semibold">{featureTitle}</strong>? Any meeting
        links to this feature will be removed too.
      </p>
      <div className="flex items-center gap-1.5">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          disabled={deleteFeature.isPending}
          onClick={() => setArmed(false)}
        >
          Cancel
        </Button>
        <Button
          type="button"
          variant="destructive"
          size="sm"
          disabled={deleteFeature.isPending}
          onClick={() => {
            deleteFeature.mutate(featureId, {
              onSuccess: () => onDeleted(),
            });
          }}
        >
          <Trash2 className="size-3.5" />
          {deleteFeature.isPending ? "Deleting…" : "Confirm delete"}
        </Button>
      </div>
    </div>
  );
}

/** Remount when server-backed values change so local state stays in sync without effects. */
function featureEditFormKey(row: FeatureRow) {
  return `${row.id}:${row.title}:${row.subtitle ?? ""}:${row.description ?? ""}:${row.notes ?? ""}`;
}

function FeatureEditForm({
  row,
  mutate,
  isPending,
}: {
  row: FeatureRow;
  mutate: (input: { id: string; patch: Partial<FeatureRow> }) => void;
  isPending: boolean;
}) {
  const [title, setTitle] = useState(row.title);
  const [subtitle, setSubtitle] = useState(row.subtitle ?? "");
  const [description, setDescription] = useState(row.description ?? "");
  const [notes, setNotes] = useState(row.notes ?? "");

  const titleTrim = title.trim();
  const subtitleNorm = subtitle.trim() === "" ? null : subtitle.trim();
  const descriptionNorm = description.trim() === "" ? null : description.trim();
  const notesNorm = notes.trim() === "" ? null : notes.trim();
  const titleDirty = titleTrim !== row.title;
  const subtitleDirty = subtitleNorm !== (row.subtitle ?? null);
  const descriptionDirty = descriptionNorm !== (row.description ?? null);
  const notesDirty = notesNorm !== (row.notes ?? null);
  const dirty = titleDirty || subtitleDirty || descriptionDirty || notesDirty;
  const canSave = dirty && titleTrim.length > 0 && !isPending;

  return (
    <div className="space-y-3 rounded-md border border-black/8 bg-black/[0.02] p-3">
      <div className="space-y-1.5">
        <Label htmlFor={`feature-title-${row.id}`}>Name</Label>
        <Input
          id={`feature-title-${row.id}`}
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Feature name"
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`feature-subtitle-${row.id}`}>Subtitle</Label>
        <Input
          id={`feature-subtitle-${row.id}`}
          value={subtitle}
          onChange={(e) => setSubtitle(e.target.value)}
          placeholder="Short supporting line (optional)"
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`feature-description-${row.id}`}>Description</Label>
        <Textarea
          id={`feature-description-${row.id}`}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={4}
          placeholder="What this feature is and why it matters"
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor={`feature-notes-${row.id}`}>Notes</Label>
        <Textarea
          id={`feature-notes-${row.id}`}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          placeholder="Classification rationale — Salesforce candidate, extend WMS lot tracking, …"
        />
      </div>
      <Button
        type="button"
        size="sm"
        disabled={!canSave}
        onClick={() => {
          mutate({
            id: row.id,
            patch: {
              title: titleTrim,
              subtitle: subtitleNorm,
              description: descriptionNorm,
              notes: notesNorm,
            },
          });
        }}
      >
        Save changes
      </Button>
    </div>
  );
}

function FeatureInfoBody({ featureId, onDeleted }: { featureId: string; onDeleted: () => void }) {
  const detailQuery = useFeatureDetail(featureId);
  const row = detailQuery.data;
  const updateFeature = useUpdateFeature();

  const sourceMeetingQuery = useQuery({
    queryKey: ["feature-source-meeting", featureId],
    queryFn: () => fetchSourceMeeting(featureId),
  });

  if (!row) {
    return (
      <p className="text-muted-foreground text-sm">
        {detailQuery.isError ? "Could not load this feature." : "Loading…"}
      </p>
    );
  }

  const domain = row.domain as Domain | null;
  const boundComponent = getArchitectureNodeById(row.component_id);

  return (
    <>
      <DialogHeader>
        <DialogTitle className="leading-snug">Feature details</DialogTitle>
        <DialogDescription className="flex flex-wrap items-center gap-1.5 pt-1">
          {domain ? <DomainPill domain={domain} size="sm" /> : null}
          {boundComponent ? (
            <span
              title={`Bound to architecture component: ${boundComponent.name}`}
              className="font-data inline-flex items-center gap-1 rounded bg-black/5 px-1.5 py-0.5 text-[10px] font-medium tracking-tight"
            >
              <span className="bg-foreground/40 size-1.5 rounded-full" aria-hidden />
              {boundComponent.name}
            </span>
          ) : null}
          {row.journey ? (
            <span className="font-data inline-flex items-center rounded bg-black/5 px-1.5 py-0.5 text-[10px] font-medium tracking-wide uppercase">
              {row.journey}
            </span>
          ) : null}
          <span className="font-data inline-flex items-center rounded bg-black/5 px-1.5 py-0.5 text-[10px] font-medium tracking-wide uppercase">
            {row.status}
          </span>
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4 pt-2">
        <FeatureEditForm
          key={featureEditFormKey(row)}
          row={row}
          mutate={updateFeature.mutate}
          isPending={updateFeature.isPending}
        />

        <div className="flex items-start gap-2 rounded-md border border-black/8 px-3 py-2.5">
          <Checkbox
            id={`feature-review-${row.id}`}
            checked={row.review_acknowledged}
            disabled={updateFeature.isPending}
            onCheckedChange={(v) => {
              if (v === "indeterminate") return;
              const next = v === true;
              if (next === row.review_acknowledged) return;
              updateFeature.mutate({ id: row.id, patch: { review_acknowledged: next } });
            }}
            className="mt-0.5"
          />
          <Label
            htmlFor={`feature-review-${row.id}`}
            className="text-foreground/90 cursor-pointer text-sm leading-snug font-normal"
          >
            I have read this feature and confirm the summary is accurate enough to proceed.
          </Label>
        </div>

        <SourceMeetingRow data={sourceMeetingQuery.data} isLoading={sourceMeetingQuery.isPending} />

        <FeatureDeleteControl featureId={row.id} featureTitle={row.title} onDeleted={onDeleted} />
      </div>
    </>
  );
}

function SourceMeetingRow({
  data,
  isLoading,
}: {
  data: SourceMeeting | null | undefined;
  isLoading: boolean;
}) {
  if (isLoading) {
    return <p className="text-muted-foreground text-xs">Loading source meeting…</p>;
  }
  if (!data) {
    return <p className="text-muted-foreground text-xs italic">No source meeting linked.</p>;
  }
  return (
    <div className="border-t pt-3">
      <p className="text-muted-foreground mb-1 text-[11px] font-medium tracking-wide uppercase">
        Source meeting
      </p>
      <Link
        to="/discoveries/$slug"
        params={{ slug: data.slug }}
        className="text-foreground hover:text-foreground/80 inline-flex items-center gap-1.5 text-sm font-medium"
      >
        {data.title}
        <ExternalLink className="size-3.5" />
      </Link>
    </div>
  );
}
